/**
 * Lightweight OOXML → HTML for reading view.
 * Preserves paragraphs, basic runs, track changes (ins/del), comment markers,
 * and embedded drawings (images from word/media).
 */

import JSZip from "jszip";
import {
  decodeXmlEntities,
  tokenizeXml,
  wAttr,
} from "./extract-annotations.js";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function jcToAlign(jc?: string): string | undefined {
  if (!jc) return undefined;
  const map: Record<string, string> = {
    right: "right",
    left: "left",
    center: "center",
    both: "justify",
    distribute: "justify",
    start: "right",
    end: "left",
  };
  return map[jc] ?? undefined;
}

/** Page geometry from OOXML `w:pgSz` / `w:pgMar` (CSS px @ 96dpi). */
export type DocxPageSize = {
  widthPx: number;
  heightPx: number;
  marginTopPx: number;
  marginRightPx: number;
  marginBottomPx: number;
  marginLeftPx: number;
  /** Raw twips from the document (for debugging / future print). */
  widthTwip: number;
  heightTwip: number;
};

export type DocxRenderResult = {
  html: string;
  /** comment id → first occurrence marker id in DOM */
  commentMarkers: string[];
  pageSize: DocxPageSize;
};

/** 1 twip = 1/1440 inch; CSS px at 96dpi → twip/15 */
export function twipToCssPx(twip: number): number {
  return twip / 15;
}

/** EMUs (English Metric Units) → CSS px @ 96dpi (914400 EMU = 1 inch). */
function emuToCssPx(emu: number): number {
  return (emu / 914400) * 96;
}

const DEFAULT_PAGE: DocxPageSize = {
  widthTwip: 11906,
  heightTwip: 16838,
  widthPx: twipToCssPx(11906),
  heightPx: twipToCssPx(16838),
  marginTopPx: twipToCssPx(1440),
  marginRightPx: twipToCssPx(1440),
  marginBottomPx: twipToCssPx(1440),
  marginLeftPx: twipToCssPx(1440),
};

function attrFromTag(raw: string, localName: string): string | undefined {
  const re = new RegExp(
    `(?:w:)?${localName}\\s*=\\s*["']([^"']+)["']`,
    "i"
  );
  return re.exec(raw)?.[1];
}

/**
 * Read the last (or only) section page size / margins from document.xml.
 */
export function extractDocxPageSize(documentXml: string): DocxPageSize {
  const pgSzTags = [...documentXml.matchAll(/<w:pgSz\b[^>]*\/?>/gi)];
  const pgMarTags = [...documentXml.matchAll(/<w:pgMar\b[^>]*\/?>/gi)];
  const pgSz = pgSzTags.at(-1)?.[0];
  const pgMar = pgMarTags.at(-1)?.[0];

  const widthTwip = Number(pgSz ? attrFromTag(pgSz, "w") : NaN) || DEFAULT_PAGE.widthTwip;
  const heightTwip =
    Number(pgSz ? attrFromTag(pgSz, "h") : NaN) || DEFAULT_PAGE.heightTwip;

  const top =
    Number(pgMar ? attrFromTag(pgMar, "top") : NaN) ||
    DEFAULT_PAGE.marginTopPx * 15;
  const right =
    Number(pgMar ? attrFromTag(pgMar, "right") : NaN) ||
    DEFAULT_PAGE.marginRightPx * 15;
  const bottom =
    Number(pgMar ? attrFromTag(pgMar, "bottom") : NaN) ||
    DEFAULT_PAGE.marginBottomPx * 15;
  const left =
    Number(pgMar ? attrFromTag(pgMar, "left") : NaN) ||
    DEFAULT_PAGE.marginLeftPx * 15;

  return {
    widthTwip,
    heightTwip,
    widthPx: twipToCssPx(widthTwip),
    heightPx: twipToCssPx(heightTwip),
    marginTopPx: twipToCssPx(top),
    marginRightPx: twipToCssPx(right),
    marginBottomPx: twipToCssPx(bottom),
    marginLeftPx: twipToCssPx(left),
  };
}

type ImageMap = Map<string, { dataUrl: string; name: string }>;

function mimeForMedia(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".bmp")) return "image/bmp";
  if (lower.endsWith(".svg")) return "image/svg+xml";
  if (lower.endsWith(".emf") || lower.endsWith(".wmf")) return "application/octet-stream";
  return "image/jpeg";
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Parse word/_rels/document.xml.rels → rId → Target path under word/. */
function parseDocumentRels(relsXml: string): Map<string, string> {
  const map = new Map<string, string>();
  const re =
    /<Relationship\b[^>]*\bId\s*=\s*["']([^"']+)["'][^>]*\bTarget\s*=\s*["']([^"']+)["'][^>]*\/?>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(relsXml)) !== null) {
    const id = m[1]!;
    let target = m[2]!.replace(/\\/g, "/");
    if (target.startsWith("/")) target = target.slice(1);
    if (!target.startsWith("word/")) {
      target = target.startsWith("media/")
        ? `word/${target}`
        : `word/${target}`;
    }
    map.set(id, target);
  }
  // Also allow Target before Id
  const re2 =
    /<Relationship\b[^>]*\bTarget\s*=\s*["']([^"']+)["'][^>]*\bId\s*=\s*["']([^"']+)["'][^>]*\/?>/gi;
  while ((m = re2.exec(relsXml)) !== null) {
    const id = m[2]!;
    if (map.has(id)) continue;
    let target = m[1]!.replace(/\\/g, "/");
    if (target.startsWith("/")) target = target.slice(1);
    if (!target.startsWith("word/")) {
      target = target.startsWith("media/")
        ? `word/${target}`
        : `word/${target}`;
    }
    map.set(id, target);
  }
  return map;
}

async function loadImageMap(zip: JSZip): Promise<ImageMap> {
  const images: ImageMap = new Map();
  const relsFile =
    zip.file("word/_rels/document.xml.rels") ||
    zip.file("word/_rels/document.xml.rels".replace(/\//g, "\\"));
  if (!relsFile) return images;
  const relsXml = await relsFile.async("string");
  const rels = parseDocumentRels(relsXml);

  for (const [rId, target] of rels) {
    const file = zip.file(target);
    if (!file) continue;
    const lower = target.toLowerCase();
    if (
      !/\.(png|jpe?g|gif|webp|bmp)$/i.test(lower) &&
      !lower.includes("/media/")
    ) {
      continue;
    }
    try {
      const buf = await file.async("uint8array");
      const mime = mimeForMedia(target);
      if (mime === "application/octet-stream") continue;
      const dataUrl = `data:${mime};base64,${bytesToBase64(buf)}`;
      images.set(rId, { dataUrl, name: target.split("/").pop() || target });
    } catch {
      /* skip broken media */
    }
  }
  return images;
}

function attrAny(raw: string, names: string[]): string | undefined {
  for (const name of names) {
    const re = new RegExp(
      `(?:\\s|:)${name.replace(":", "\\:")}\\s*=\\s*["']([^"']+)["']`,
      "i"
    );
    const m = re.exec(raw);
    if (m?.[1]) return m[1];
  }
  return undefined;
}

/**
 * Convert word/document.xml into readable HTML with TC + comment anchors + images.
 */
export function documentXmlToHtml(
  documentXml: string,
  images: ImageMap = new Map()
): DocxRenderResult {
  const tokens = tokenizeXml(documentXml);
  const parts: string[] = [];
  const commentMarkers: string[] = [];

  let skipDrawingDepth = 0;
  let drawingBuf: string[] = [];
  let inDrawing = false;
  let inT = false;
  let inDelText = false;
  let paraOpen = false;
  let paraAlign: string | undefined;
  let runOpen = false;
  const revStack: Array<{
    type: "ins" | "del";
    author: string;
    date?: string;
  }> = [];
  const activeComments = new Set<string>();

  const openPara = (align?: string) => {
    if (paraOpen) return;
    const style = align ? ` style="text-align:${align}"` : "";
    parts.push(`<p class="nr-p"${style}>`);
    paraOpen = true;
    paraAlign = align;
  };

  const closePara = () => {
    if (!paraOpen) return;
    if (runOpen) {
      parts.push("</span>");
      runOpen = false;
    }
    parts.push("</p>");
    paraOpen = false;
    paraAlign = undefined;
  };

  const ensurePara = () => {
    if (!paraOpen) openPara();
  };

  const wrapRevOpen = () => {
    const top = revStack[revStack.length - 1];
    if (!top) return;
    const authorAttr = escapeHtml(top.author || "ناشناس");
    const dateAttr = top.date ? ` data-date="${escapeHtml(top.date)}"` : "";
    const title =
      top.type === "ins"
        ? `درج — ${authorAttr}`
        : `حذف — ${authorAttr}`;
    if (top.type === "ins") {
      parts.push(
        `<ins class="nr-ins" data-author="${authorAttr}"${dateAttr} title="${title}">`
      );
    } else {
      parts.push(
        `<del class="nr-del" data-author="${authorAttr}"${dateAttr} title="${title}">`
      );
    }
  };

  const wrapRevClose = () => {
    const top = revStack[revStack.length - 1];
    if (top?.type === "ins") parts.push("</ins>");
    else if (top?.type === "del") parts.push("</del>");
  };

  const pushText = (raw: string) => {
    if (!raw) return;
    ensurePara();
    const text = escapeHtml(decodeXmlEntities(raw));
    if (activeComments.size > 0) {
      const ids = [...activeComments].join(" ");
      parts.push(
        `<mark class="nr-comment-range" data-comment-ids="${ids}">${text}</mark>`
      );
    } else {
      parts.push(text);
    }
  };

  const flushDrawing = () => {
    const blob = drawingBuf.join("");
    drawingBuf = [];
    inDrawing = false;
    const embed =
      attrAny(blob, ["r:embed", "r:link"]) ||
      /(?:r:embed|r:link)\s*=\s*["']([^"']+)["']/i.exec(blob)?.[1];
    if (!embed) return;
    const img = images.get(embed);
    if (!img) return;
    const cx = Number(
      /(?:\bcx|a:ext[^>]*\bcx)\s*=\s*["'](\d+)["']/i.exec(blob)?.[1] ?? NaN
    );
    const cy = Number(
      /(?:\bcy|a:ext[^>]*\bcy)\s*=\s*["'](\d+)["']/i.exec(blob)?.[1] ?? NaN
    );
    const w = Number.isFinite(cx) && cx > 0 ? Math.round(emuToCssPx(cx)) : undefined;
    const h = Number.isFinite(cy) && cy > 0 ? Math.round(emuToCssPx(cy)) : undefined;
    const styleParts: string[] = ["max-width:100%", "height:auto"];
    if (w) styleParts.push(`width:${Math.min(w, 720)}px`);
    if (h && w) styleParts.push(`aspect-ratio:${w}/${h}`);
    ensurePara();
    parts.push(
      `<img class="nr-img" src="${img.dataUrl}" alt="${escapeHtml(img.name)}" style="${styleParts.join(";")}" loading="lazy" />`
    );
  };

  for (const tok of tokens) {
    if (tok.kind === "open") {
      const n = tok.tag.name;
      if (n === "drawing" || n === "pict" || n === "object") {
        if (!tok.tag.selfClosing) {
          skipDrawingDepth += 1;
          inDrawing = true;
          drawingBuf = [tok.tag.raw];
        }
        continue;
      }
      if (skipDrawingDepth > 0) {
        drawingBuf.push(`<${tok.tag.raw}>`);
        continue;
      }

      if (n === "p") {
        openPara();
      } else if (n === "jc") {
        const val = wAttr(tok.tag.raw, "val");
        const align = jcToAlign(val);
        if (align && paraOpen) {
          const last = parts[parts.length - 1];
          if (last?.startsWith('<p class="nr-p"')) {
            parts[parts.length - 1] =
              `<p class="nr-p" style="text-align:${align}" data-align="${align}">`;
          }
          paraAlign = align;
        }
      } else if (n === "commentRangeStart") {
        const id = wAttr(tok.tag.raw, "id");
        if (id != null) {
          activeComments.add(id);
          if (!commentMarkers.includes(id)) commentMarkers.push(id);
          ensurePara();
          parts.push(
            `<span class="nr-comment-anchor" id="nr-cmt-${id}" data-comment-id="${id}" aria-hidden="true"></span>`
          );
        }
      } else if (n === "commentRangeEnd") {
        const id = wAttr(tok.tag.raw, "id");
        if (id != null) activeComments.delete(id);
      } else if (n === "ins" || n === "moveTo") {
        if (!tok.tag.selfClosing) {
          ensurePara();
          revStack.push({
            type: "ins",
            // XML entities decoded here; escapeHtml re-encodes on output
            author: decodeXmlEntities(wAttr(tok.tag.raw, "author") ?? "Word"),
            date: decodeXmlEntities(wAttr(tok.tag.raw, "date") ?? "") || undefined,
          });
          wrapRevOpen();
        }
      } else if (n === "del" || n === "moveFrom") {
        if (!tok.tag.selfClosing) {
          ensurePara();
          revStack.push({
            type: "del",
            // XML entities decoded here; escapeHtml re-encodes on output
            author: decodeXmlEntities(wAttr(tok.tag.raw, "author") ?? "Word"),
            date: decodeXmlEntities(wAttr(tok.tag.raw, "date") ?? "") || undefined,
          });
          wrapRevOpen();
        }
      } else if (n === "t") {
        inT = true;
      } else if (n === "delText") {
        inDelText = true;
      } else if (n === "tab") {
        pushText("\t");
      } else if (n === "br" || n === "cr") {
        ensurePara();
        parts.push("<br/>");
      } else if (n === "tbl") {
        closePara();
        parts.push('<div class="nr-table-wrap"><table class="nr-table">');
      } else if (n === "tr") {
        parts.push("<tr>");
      } else if (n === "tc") {
        parts.push("<td>");
      }
    } else if (tok.kind === "close") {
      const n = tok.name;
      if (n === "drawing" || n === "pict" || n === "object") {
        if (skipDrawingDepth > 0) {
          drawingBuf.push(`</${n}>`);
          skipDrawingDepth -= 1;
          if (skipDrawingDepth === 0 && inDrawing) flushDrawing();
        }
        continue;
      }
      if (skipDrawingDepth > 0) {
        drawingBuf.push(`</${n}>`);
        continue;
      }

      if (n === "t") {
        inT = false;
      } else if (n === "delText") {
        inDelText = false;
      } else if (
        n === "ins" ||
        n === "moveTo" ||
        n === "del" ||
        n === "moveFrom"
      ) {
        wrapRevClose();
        revStack.pop();
      } else if (n === "p") {
        closePara();
      } else if (n === "tc") {
        closePara();
        parts.push("</td>");
      } else if (n === "tr") {
        parts.push("</tr>");
      } else if (n === "tbl") {
        parts.push("</table></div>");
      }
    } else if (tok.kind === "text") {
      if (skipDrawingDepth > 0) {
        drawingBuf.push(tok.value);
        continue;
      }
      if (inT || inDelText) {
        pushText(tok.value);
      }
    }
  }

  closePara();
  void paraAlign;

  return {
    html: `<article class="nr-docx" dir="auto">${parts.join("")}</article>`,
    commentMarkers,
    pageSize: DEFAULT_PAGE,
  };
}

export async function renderDocxToHtml(
  buffer: ArrayBuffer | Uint8Array
): Promise<DocxRenderResult> {
  const zip = await JSZip.loadAsync(buffer);
  const documentFile = zip.file("word/document.xml");
  if (!documentFile) {
    throw new Error("DOCX فاقد word/document.xml است");
  }
  const documentXml = await documentFile.async("string");
  const images = await loadImageMap(zip);
  const result = documentXmlToHtml(documentXml, images);
  return {
    ...result,
    pageSize: extractDocxPageSize(documentXml),
  };
}
